/**
 * Fetch gateway deployments for the current org (RLS-scoped).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  createDeployment,
  advanceDeployment,
  pauseDeployment,
  resumeDeployment,
  cancelDeployment,
  type CreateDeploymentInput,
} from '@/services/gateway';
import type { GatewayDeployment } from '@/types/gateway';

const DEPLOYMENTS_QUERY_KEY = ['gateway-deployments'];

export function useGatewayDeployments() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: DEPLOYMENTS_QUERY_KEY,
    queryFn: async (): Promise<GatewayDeployment[]> => {
      const { data, error } = await supabase
        .from('gateway_deployments')
        .select(`
          id,
          organization_id,
          firmware_release_id,
          name,
          strategy,
          staged_config,
          maintenance_window,
          status,
          total_gateways,
          updated_gateways,
          failed_gateways,
          failure_threshold_pct,
          created_at,
          started_at,
          completed_at,
          paused_at,
          created_by,
          gateway_firmware_releases(version, release_notes)
        `)
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as (Omit<GatewayDeployment, 'firmware_version' | 'release_notes'> & {
        gateway_firmware_releases: { version: string; release_notes: string | null } | null;
      })[];
      return rows.map((r) => ({
        ...r,
        firmware_version: r.gateway_firmware_releases?.version ?? undefined,
        release_notes: r.gateway_firmware_releases?.release_notes ?? undefined,
        gateway_firmware_releases: undefined,
      })) as GatewayDeployment[];
    },
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateDeploymentInput) => createDeployment(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEPLOYMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['gateway-devices'] });
    },
  });

  const advanceMutation = useMutation({
    mutationFn: (deploymentId: string) => advanceDeployment(deploymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEPLOYMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['gateway-deployment'] });
    },
  });

  const pauseMutation = useMutation({
    mutationFn: (deploymentId: string) => pauseDeployment(deploymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEPLOYMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['gateway-deployment'] });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: (deploymentId: string) => resumeDeployment(deploymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEPLOYMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['gateway-deployment'] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (deploymentId: string) => cancelDeployment(deploymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEPLOYMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['gateway-deployment'] });
    },
  });

  return {
    deployments: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
    createDeployment: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    createError: createMutation.error as Error | null,
    advanceDeployment: advanceMutation.mutateAsync,
    pauseDeployment: pauseMutation.mutateAsync,
    resumeDeployment: resumeMutation.mutateAsync,
    cancelDeployment: cancelMutation.mutateAsync,
  };
}
